/**
 * Sources — where this organization's opportunity finder looks.
 *
 * These replace the "Intent Sources" mock that used to sit under Program &
 * Provider Import: every row here is a real source the scan reads, scoped to
 * this organization, and turning one off removes it from the next scan.
 */
import React, { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { SectionHeader, PageState } from '@/components/weavein';
import { SOURCE_COPY, type FundingApi, type IntentSource } from '@/services/api/fundingOpportunities';
import { CARD, MUTED, ErrorNote, formatDate, useLoad } from './shared';

const SourcesPanel: React.FC<{ api: FundingApi }> = ({ api }) => {
  const { data, error, loading, setData } = useLoad(() => api.listSources(), [api]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const toggle = async (s: IntentSource, enabled: boolean) => {
    setBusy(s.kind);
    setActionError(null);
    try {
      const next = await api.setSource(s.kind, enabled);
      setData((cur) => (cur ?? []).map((x) => (x.kind === s.kind ? next : x)));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`${CARD} space-y-3 p-4`}>
      <SectionHeader
        title="Where we look for funders"
        description="Each scan reads the sources you leave on. Program and sponsor data comes from the platform's Program & Provider Import."
      />
      {error && <ErrorNote message={error} />}
      {actionError && <ErrorNote message={actionError} />}
      {loading && !data && <PageState variant="loading" />}
      {data && data.length === 0 && <PageState variant="empty" title="No sources" message="Sources appear here once the finder is set up for your organization." />}
      <ul>
        {(data ?? []).map((s) => {
          const copy = SOURCE_COPY[s.kind] ?? { name: s.kind, detail: '' };
          return (
            <li key={s.kind} className="flex items-start justify-between gap-4 border-b border-v2border-muted py-3 last:border-0">
              <div className="min-w-0">
                <p className="text-[14px] font-medium text-ink">{copy.name}</p>
                <p className={MUTED}>{copy.detail}</p>
                <p className="mt-1 text-[12px] text-ink-400">
                  {s.last_scanned_at
                    ? `Last scanned ${formatDate(s.last_scanned_at)} · ${s.last_found ?? 0} matches`
                    : 'Not scanned yet'}
                </p>
              </div>
              <Switch
                checked={s.enabled}
                disabled={busy === s.kind}
                onCheckedChange={(v: boolean) => toggle(s, v)}
                aria-label={`Use ${copy.name}`}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SourcesPanel;
